import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Home, ChevronRight } from 'lucide-react';
import {
  Breadcrumb as BreadcrumbComponent,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';

interface BreadcrumbProps {
  isAdmin?: boolean;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ isAdmin = false }) => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Define route labels
  const routeLabels: Record<string, string> = {
    // Admin routes
    admin: 'Admin',
    overview: 'Overview',
    churches: 'Churches',
    users: 'Global Users',
    reports: 'Reports',
    billing: 'Billing & Plans',
    system: 'System Health',
    audit: 'Audit Logs',
    settings: 'Settings',
    new: 'Create New',
    
    // Directory routes
    directory: 'Directory',
    families: 'Families',
    groups: 'Groups',
    birthdays: 'Birthdays',
    anniversaries: 'Anniversaries',
    print: 'Print Directory',
    changes: 'Change Requests',
    me: 'My Account',
    
    // Regular user routes
    members: 'Members',
    events: 'Events',
    prayers: 'Prayer Wall',
    attendance: 'Attendance',
    messages: 'Messages',
    profile: 'Profile',
  };

  const homeRoute = '/';
  const homeLabel = isAdmin ? 'Admin Dashboard' : 'Dashboard';

  // Build breadcrumb items
  const breadcrumbItems = pathSegments.map((segment, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/');
    const label = routeLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1);
    const isLast = index === pathSegments.length - 1;

    return {
      path,
      label,
      isLast,
    };
  });

  // Don't show breadcrumb on home pages
  if (location.pathname === '/') {
    return (
      <div className="flex items-center justify-between mb-6 px-6 pt-6">
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{homeLabel}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between mb-6 px-6 pt-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to={homeRoute}>
            <Home className="h-4 w-4" />
          </Link>
        </Button>
        
        <BreadcrumbComponent>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={homeRoute}>{isAdmin ? 'Admin' : 'Home'}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            
            {breadcrumbItems.slice(isAdmin ? 1 : 0).map((item, index) => (
              <React.Fragment key={item.path}>
                <BreadcrumbSeparator>
                  <ChevronRight />
                </BreadcrumbSeparator>
                <BreadcrumbItem>
                  {item.isLast ? (
                    <BreadcrumbPage>{item.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={item.path}>{item.label}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </BreadcrumbComponent>
      </div>
    </div>
  );
};

export default Breadcrumb;